import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";

/**
 * Reusable custom dropdown for admin forms.
 * White card menu, charcoal text, 12px rounded, soft shadow,
 * green glow on focus/open, full keyboard support.
 *
 * Props: { value, onChange, options:[{value,label}], placeholder, testid }
 */
export default function Select({
  value,
  onChange,
  options,
  placeholder = "Seleziona",
  testid,
  required = false,
}) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const wrapRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value),
    [options, value]
  );

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      const i = options.findIndex((o) => o.value === value);
      setHi(i >= 0 ? i : 0);
    }
  }, [open, options, value]);

  const select = (v) => {
    onChange(v);
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === "Escape") return setOpen(false);
    if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
      e.preventDefault();
      return setOpen(true);
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHi((h) => Math.min(options.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = options[hi];
      if (opt) select(opt.value);
    }
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <button
        type="button"
        data-testid={testid}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-required={required}
        onClick={() => setOpen((s) => !s)}
        onKeyDown={onKey}
        className={`w-full text-left px-4 py-3 rounded-xl bg-white border text-sm font-medium transition-all flex items-center justify-between gap-2 ${
          open
            ? "border-[#76BA1B] shadow-[0_0_0_4px_rgba(118,186,27,0.15)]"
            : "border-slate-200 hover:border-[#76BA1B]/50 hover:shadow-[0_0_0_4px_rgba(118,186,27,0.08)]"
        }`}
      >
        <span className={`truncate ${selected ? "text-[#1e293b]" : "text-slate-400"}`}>
          {selected?.label || placeholder}
        </span>
        <ChevronDown
          size={14}
          className={`text-slate-500 transition-transform duration-200 flex-shrink-0 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          data-testid={testid ? `${testid}-menu` : undefined}
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 max-h-72 overflow-y-auto bg-white p-1.5 animate-fade-in"
          style={{
            borderRadius: "12px",
            boxShadow:
              "0 10px 25px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.05)",
            border: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          {options.map((o, i) => {
            const isSel = o.value === value;
            const isHi = i === hi;
            return (
              <li
                key={o.value ?? `__${i}`}
                role="option"
                aria-selected={isSel}
                data-testid={testid ? `${testid}-option-${o.value || "any"}` : undefined}
                onMouseEnter={() => setHi(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(o.value);
                }}
                className={`flex items-center justify-between gap-3 rounded-lg cursor-pointer select-none transition-colors duration-150 text-sm ${
                  isHi ? "bg-[#f0fdf4]" : "bg-transparent"
                } text-[#1e293b]`}
                style={{ padding: "12px 15px" }}
              >
                <span className="truncate">{o.label}</span>
                {isSel && <Check size={14} className="text-[#76BA1B] flex-shrink-0" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
